import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParallaxRef } from '@/utils/parallax';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { Badge } from '@/components/ui/badge';
import { MagicCard } from '@/components/lib/magicui/MagicCard';
import { ExternalLink } from 'lucide-react';

interface PortfolioProject {
  id: string;
  title: string;
  domain: string;
  niche: string;
  outcome: string;
  packageKey: 'starter' | 'basic' | 'pro';
  packageLabel: string;
  url?: string;
}

const projects: PortfolioProject[] = [];

type FilterType = 'all' | 'starter' | 'basic' | 'pro';

export default function ServicesPortfolioSection() {
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  // Parallax for subtitle
  const subtitleRef = useParallaxRef({
    speed: -0.12,
    axis: 'y',
    maxTranslate: 10,
  });

  // Parallax for H2 (subtle)
  const titleRef = useParallaxRef({
    speed: -0.06,
    axis: 'y',
    maxTranslate: 6,
  });

  // Reveal hook for section entrance
  const sectionReveal = useRevealOnce({ amount: 0.2 });

  // Filter projects
  const filteredProjects =
    activeFilter === 'all'
      ? projects
      : projects.filter((p) => p.packageKey === activeFilter);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' as const },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: { duration: 0.3 },
    },
  };

  const filters: { key: FilterType; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'starter', label: 'Starter Branding' },
    { key: 'basic', label: 'Basic Site Builds' },
    { key: 'pro', label: 'Pro Launch Studio' },
  ];

  // No projects to show: the section (heading included) does not render
  if (projects.length === 0) return null;

  return (
    <section className="relative w-full bg-black text-slate-100 py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            ref={sectionReveal.ref}
            variants={containerVariants}
            initial="hidden"
            animate={sectionReveal.controls}
          >
            {/* Heading Section */}
            <div className="text-center mb-12 md:mb-16">
              <h2
                ref={titleRef as any}
                className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50 mb-6"
              >
                Portfolio & Results
              </h2>
              <p
                ref={subtitleRef as any}
                className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto"
              >
                Real projects, real results. See how we've helped solo pros and small firms turn their domains into client-getting systems.
              </p>
            </div>

            {/* Filter Pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-12 md:mb-16">
              {filters.map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setActiveFilter(filter.key)}
                  className={`
                    px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                    ${
                      activeFilter === filter.key
                        ? 'bg-white text-black shadow-lg'
                        : 'border border-slate-700 text-slate-400 bg-transparent hover:border-slate-600 hover:text-slate-300'
                    }
                  `}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Bento Grid */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFilter}
                variants={containerVariants}
                initial="hidden"
                animate="show"
                exit="hidden"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
              >
                {filteredProjects.map((project) => (
                  <motion.div key={project.id} variants={cardVariants}>
                    <MagicCard className="h-full p-6">
                      <div className="flex flex-col h-full">
                        {/* Package Badge */}
                        <div className="mb-4">
                          <Badge
                            variant="outline"
                            className="bg-slate-800/50 border-slate-700 text-slate-300 text-xs"
                          >
                            {project.packageLabel}
                          </Badge>
                        </div>

                        {/* Project Title */}
                        <h3 className="text-xl font-semibold text-slate-50 mb-2">
                          {project.title}
                        </h3>

                        {/* Domain */}
                        <p className="text-sm text-blue-400 mb-3 font-mono">
                          {project.domain}
                        </p>

                        {/* Niche */}
                        <p className="text-xs text-slate-400 uppercase tracking-wide mb-4">
                          {project.niche}
                        </p>

                        {/* Outcome */}
                        <div className="mt-auto pt-4 border-t border-slate-800">
                          <p className="text-sm font-semibold text-slate-50 mb-1">
                            Outcome:
                          </p>
                          <p className="text-sm text-slate-300 leading-relaxed">
                            {project.outcome}
                          </p>
                        </div>

                        {/* Optional View Link */}
                        {project.url && (
                          <a
                            href={project.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            View live site <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </MagicCard>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

